import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Box,
  Avatar,
  Divider,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {Autocomplete} from "@react-google-maps/api";
import {useLocation} from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AddLocationIcon from "@mui/icons-material/AddLocation";

const theme = createTheme({
  typography: {
    fontFamily: "'Inter', sans-serif",
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
    },
  },
});

export const NavBar = ({
  user,
  handleLogin,
  handleLogout,
  navigate,
  setAutocomplete,
  handleCitySearch,
}) => {
  const location = useLocation();
  const showSearch = location.pathname === "/map";
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <ThemeProvider theme={theme}>
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{
        backgroundColor: "#C41E3A",
        borderBottom: "none",
        boxShadow: "none",
        zIndex: 1000,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "72px",
          padding: { xs: "8px 16px", sm: "12px 24px", md: "12px 32px" },
          gap: "16px",
          maxWidth: "1400px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {/* Left Side: Logo and Search Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "12px", sm: "20px" },
            flex: showSearch ? 1 : "none",
            minWidth: 0,
          }}
        >
          {/* App Title and Icon */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": {
                opacity: 0.9,
              },
              transition: "opacity 0.2s ease",
            }}
            onClick={() => navigate("/")}
          >
            <Box
              sx={{
                width: { xs: "48px", sm: "52px" },
                height: { xs: "48px", sm: "52px" },
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "none",
                border: "2px solid #C41E3A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#FFFFFF",
                padding: "4px",
              }}
            >
              <img
                src="/logo.png?v=2"
                alt="Christmas Lights Map Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  // Fallback if logo.png doesn't load
                  e.target.src = "/logo-icon.png?v=2";
                }}
              />
            </Box>
        
          </Box>

          {/* City Search Autocomplete - Only show on map page */}
          {showSearch && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { xs: "100%", sm: "400px", md: "500px" },
                position: "relative",
                zIndex: 1100,
              }}
            >
              <Autocomplete
                onLoad={(instance) => {
                  setAutocomplete(instance);
                }}
                onPlaceChanged={handleCitySearch}
              >
                <TextField
                  id="city-search"
                  label="Search City"
                  variant="outlined"
                  size="small"
                  placeholder="Enter city name..."
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      borderRadius: "12px",
                      fontSize: "15px",
                      transition: "all 0.2s ease",
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        borderWidth: "1.5px",
                      },
                      "&:hover": {
                        backgroundColor: "#FFFFFF",
                        "& fieldset": {
                          borderColor: "rgba(255, 255, 255, 0.5)",
                        },
                      },
                    "&.Mui-focused": {
                      backgroundColor: "#FFFFFF",
                      "& fieldset": {
                        borderColor: "#FFFFFF",
                        borderWidth: "2px",
                      },
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: "#C41E3A",
                    },
                  },
                    "& .MuiInputBase-input": {
                      padding: "10px 14px",
                    },
                  }}
                />
              </Autocomplete>
            </Box>
          )}
        </Box>

        {/* Right Side Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          {/* User Avatar */}
          {user && (
            <Avatar
              src={user.photoURL}
              alt={user.displayName || "User"}
              sx={{
                width: { xs: 38, sm: 42 },
                height: { xs: 38, sm: 42 },
                border: "2.5px solid rgba(255, 255, 255, 0.4)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                bgcolor: user.photoURL ? "transparent" : "#FFFFFF",
                color: user.photoURL ? "inherit" : "#C41E3A",
                fontSize: "16px",
                fontWeight: "600",
                "&:hover": {
                  transform: "scale(1.05)",
                  borderColor: "rgba(255, 255, 255, 0.6)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              {!user.photoURL && (() => {
                if (user.displayName) {
                  const initials = user.displayName
                    .split(" ")
                    .map((name) => name[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2);
                  return initials;
                } else if (user.email) {
                  return user.email[0].toUpperCase();
                }
                return "U";
              })()}
            </Avatar>
          )}
          
          {/* Hamburger Menu */}
          <IconButton
            size="medium"
            edge="end"
            color="inherit"
            onClick={handleMenuClick}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.25)",
                transform: "scale(1.05)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 8,
            sx: {
              mt: 1.5,
              minWidth: 220,
              borderRadius: "12px",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* Menu Items with Icons */}
          <MenuItem 
            onClick={() => {
              navigate("/");
              handleMenuClose();
            }}
            sx={{
              padding: "12px 20px",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "rgba(196, 30, 58, 0.08)",
              },
            }}
          >
            <HomeIcon sx={{ marginRight: "12px", color: "#C41E3A", fontSize: "22px" }} />
            Home
          </MenuItem>
          
          <MenuItem 
            onClick={() => {
              navigate("/map");
              handleMenuClose();
            }}
            sx={{
              padding: "12px 20px",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "rgba(196, 30, 58, 0.08)",
              },
            }}
          >
            <MapIcon sx={{ marginRight: "12px", color: "#C41E3A", fontSize: "22px" }} />
            Map
          </MenuItem>
          
          <MenuItem 
            onClick={() => {
              navigate("/help");
              handleMenuClose();
            }}
            sx={{
              padding: "12px 20px",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "rgba(196, 30, 58, 0.08)",
              },
            }}
          >
            <HelpOutlineIcon sx={{ marginRight: "12px", color: "#C41E3A", fontSize: "22px" }} />
            Help
          </MenuItem>
          
          <MenuItem 
            onClick={() => {
              navigate("/top-picks");
              handleMenuClose();
            }}
            sx={{
              padding: "12px 20px",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "rgba(196, 30, 58, 0.08)",
              },
            }}
          >
            <LeaderboardIcon sx={{ marginRight: "12px", color: "#C41E3A", fontSize: "22px" }} />
            Top Picks
          </MenuItem>
          
          <MenuItem 
            onClick={() => {
              if (user) {
                navigate("/add-house");
              } else {
                navigate("/login?redirect=/add-house");
              }
              handleMenuClose();
            }}
            sx={{
              padding: "12px 20px",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "rgba(196, 30, 58, 0.08)",
              },
            }}
          >
            <AddLocationIcon sx={{ marginRight: "12px", color: "#C41E3A", fontSize: "22px" }} />
            Add Location
          </MenuItem>
          
          <Divider sx={{ my: 0.5 }} />
          
          {user ? (
            <MenuItem 
              onClick={() => {
                handleLogout();
                handleMenuClose();
              }}
              sx={{
                padding: "12px 20px",
                fontSize: "15px",
                color: "#c41e3a",
                "&:hover": {
                  backgroundColor: "rgba(196, 30, 58, 0.08)",
                },
              }}
            >
              <LogoutIcon sx={{ marginRight: "12px", fontSize: "22px" }} />
              Logout
            </MenuItem>
          ) : (
            <MenuItem 
              onClick={() => {
                handleLogin();
                handleMenuClose();
              }}
              sx={{
                padding: "12px 20px",
                fontSize: "15px",
                color: "#C41E3A",
                "&:hover": {
                  backgroundColor: "rgba(196, 30, 58, 0.08)",
                },
              }}
            >
              <LoginIcon sx={{ marginRight: "12px", fontSize: "22px" }} />
              Login
            </MenuItem>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
    </ThemeProvider>
  );
};
